/**
 * Businesses advanced-filter document — a versioned value the web filter
 * panel edits and evaluates CLIENT-SIDE over the complete business index. One
 * schema + ONE evaluator, both in `@releve/shared` (the vault / chart-of-
 * accounts template on `filterDocumentPrimitives.ts`), shared by:
 *  - the web filter panel (live evaluation as controls change),
 *  - any future server consumer (reuse `applyBusinessFilterDocument` — never
 *    build SQL WHERE clauses from the document).
 *
 * The document is AD HOC — it lives in component state for the session. There
 * is NO seeded default: the grid opens on every business, so
 * `EMPTY_BUSINESS_FILTER_DOCUMENT` is the only starting value.
 *
 * v1 vocabulary: business type, country, city (string `in` / `is_empty`
 * primitives) and ACTIVITY — a boolean arm (`op: 'eq'`) no shared primitive
 * covers, spelled locally the way `placesFilters.ts` spells its boolean
 * family. Activity is DERIVED (`transaction_count > 0`), never a view column.
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b the grid runs this panel and ZERO
 * filterable columns. Fields are `business.<field>` over the MAPPED grid rows;
 * evaluation runs AFTER the mapper and BEFORE the grid shell
 * (`hasUpstreamFilters`).
 *
 * `countActiveBusinessConditions` carries the entity in its name because the
 * bare `countActiveConditions` is already exported from the barrel by
 * `placesFilters.ts`.
 */
import { z } from "zod";
import { makeFilterDocumentSchema, makeStringConditionSchema, matchesStringOp, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1)
// ---------------------------------------------------------------------------
export const BUSINESS_FILTER_STRING_FIELDS = [
    "business.business_type_name",
    "business.country",
    "business.city",
];
/** Derived booleans — `has_activity` = `transaction_count > 0`. */
export const BUSINESS_FILTER_BOOLEAN_FIELDS = ["business.has_activity"];
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
/**
 * TWO families with DISJOINT op sets ('in'/'is_empty' vs 'eq'), so the
 * evaluator narrows on `op` alone. Empty-set semantics come from the shared
 * string primitive: "nothing checked" is NO condition, never an `in` with zero
 * values.
 */
const stringConditionSchema = makeStringConditionSchema(BUSINESS_FILTER_STRING_FIELDS);
const booleanConditionSchema = z.object({
    field: z.enum(BUSINESS_FILTER_BOOLEAN_FIELDS),
    op: z.literal("eq"),
    value: z.boolean(),
});
export const businessFilterConditionSchema = z.union([
    stringConditionSchema,
    booleanConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const businessFilterDocumentSchema = makeFilterDocumentSchema(businessFilterConditionSchema);
/** The empty document — "no filters active" (what Clear resets to). */
export const EMPTY_BUSINESS_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/*
 * One accessor per field family, each an exhaustive switch with NO default
 * case: a new vocabulary entry is a TS2366 until it is handled here.
 */
function stringValue(row, field) {
    switch (field) {
        case "business.business_type_name":
            return row.business_type_name ?? "";
        case "business.country":
            return row.country ?? "";
        case "business.city":
            return row.city ?? "";
    }
}
function booleanValue(row, field) {
    switch (field) {
        case "business.has_activity":
            return row.transaction_count > 0;
    }
}
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "eq":
            return booleanValue(row, c.field) === c.value;
    }
}
/**
 * Apply a filter document to business rows (AND of all conditions — v1
 * `match` is 'all' only). An empty document returns the input array unchanged
 * (identity — callers can rely on reference equality to skip re-renders).
 */
export function applyBusinessFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveBusinessConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=businessFilters.js.map